// src/utils.js

// 日付フォーマット YYYY-MM-DD
export const formatDateToIso = (date) => {
  if (!date) return '';
  const d = new Date(date);
  const y = d.getFullYear();
  const m = ('0' + (d.getMonth() + 1)).slice(-2);
  const day = ('0' + d.getDate()).slice(-2);
  return `${y}-${m}-${day}`;
};

// 曜日取得
export const getDayOfWeek = (isoDate) => {
  if (!isoDate) return '';
  const date = new Date(isoDate);
  const days = ['日', '月', '火', '水', '木', '金', '土'];
  return days[date.getDay()];
};

// YouTube ID抽出
export const getYouTubeId = (url) => {
  if (!url) return null;
  const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
  const match = url.match(regExp);
  return (match && match[2].length === 11) ? match[2] : null;
};

// メンバー情報の取得系ヘルパー
export const getMemberName = (m) => {
  if (!m) return '不明';
  if (typeof m === 'object') return m.name || '不明';
  return m;
};

// ★変更: null を渡された場合に typeof null === 'object' で落ちるのを防ぐ
export const getMemberRole = (m) => {
  if (!m) return 'FP';
  if (typeof m === 'object') return m.role || 'FP';
  return 'FP';
};

// ★変更: 同上（null ガード）
export const getMemberPic = (m) => {
  if (!m) return null;
  if (typeof m === 'object') return m.pictureUrl || null;
  return null;
};

// ★変更: 同上（null ガード）
export const getMemberId = (m) => {
  if (!m) return null;
  if (typeof m === 'object') return m.userId || null;
  return null;
};

// 通貨フォーマット
// ★変更: NaN / undefined を渡されたときに "NaN" と表示されるのを防ぐ
export const formatCurrency = (num) => {
  const n = Number(num);
  return (Number.isFinite(n) ? n : 0).toLocaleString();
};

// ==========================================
// Rank Logic
// ==========================================

export const DEFAULT_RANKS = [
  { id: 'monthly', name: '正規メンバー', icon: '👑', type: 'free', priority: true },
  { id: 'collaborator', name: 'ID協力者', icon: '🤝', type: 'full', priority: true },
  { id: 'visitor', name: '都度会員', icon: '👤', type: 'full', priority: false }
];

// ==========================================
// ランク判定
//
// 【データ設計】
//   rankIds : 文字列の配列。保有する全ランク。AdminUsers.js が書き込む「正」の情報。
//   rankId  : 文字列。主ランク1つ。rankIds から導出される。
//   membership : 文字列。rankId と同じ値。
//   rankExpiries : { ランクID: 有効期限のISO文字列 }
//
// 【修正した3つの問題】
//   1. 引数の破壊
//      修正前は `let ids = user.rankIds || []` と参照を掴んでいたため、
//      後続の push が user.rankIds 自体を書き換えていた。
//      getUserRank は描画のたびに呼ばれるため汚染が蓄積し、
//      Profile.js の保存で Firestore に永続化されていた。
//   2. 型の混在
//      App.js が rankId を配列で書き込むため、文字列・配列が混在していた。
//      どちらでも受け付けるようにした。
//   3. 有効期限が判定に反映されない
//      App.js の期限切れ処理は rankId しか掃除せず rankIds を放置するため、
//      期限が切れても rankIds 経由でランクが残り続けていた。
//      ここで rankExpiries を見て、期限切れを判定から除外する。
// ==========================================
export const getUserRankIds = (user) => {
    if (!user) return ['visitor'];

    // 入れ子配列・文字列以外を除去して「文字列の配列」に揃える
    const flatten = (v) => {
        if (Array.isArray(v)) return v.flat(Infinity).filter(x => typeof x === 'string' && x);
        return (typeof v === 'string' && v) ? [v] : [];
    };

    // 元の配列は絶対に触らない（引数を書き換えない）
    const ids = flatten(user.rankIds);

    if (user.isMonthly || user.membership === 'monthly') {
        if (!ids.includes('monthly')) ids.push('monthly');
    }
    if (user.isCollaborator || user.membership === 'collaborator') {
        if (!ids.includes('collaborator')) ids.push('collaborator');
    }

    flatten(user.rankId).forEach(id => {
        if (!ids.includes(id)) ids.push(id);
    });

    // ★ カスタムランク（rank_xxxxx）も membership から拾う。
    //   上の2行は monthly / collaborator しか見ていないため。
    if (typeof user.membership === 'string' && user.membership && !ids.includes(user.membership)) {
        ids.push(user.membership);
    }

    // ★ 有効期限が切れたランクを除外する
    const expiries = (user.rankExpiries && typeof user.rankExpiries === 'object') ? user.rankExpiries : {};
    const now = Date.now();
    const valid = ids.filter(id => {
        const exp = expiries[id];
        if (!exp) return true;                       // 期限の設定が無いものは無期限とみなす
        const t = new Date(exp).getTime();
        if (!Number.isFinite(t)) return true;        // 日付として壊れている場合は残す（誤って剥奪しない）
        return now <= t;
    });

    if (valid.length === 0) return ['visitor'];
    return valid;
};

export const getUserRank = (user, ranks = []) => {
  // ★変更: ranks に null が明示的に渡されると .length で落ちるため Array.isArray で判定
  const rankList = (Array.isArray(ranks) && ranks.length > 0) ? ranks : DEFAULT_RANKS;
  const userRankIds = getUserRankIds(user);
  const myRanks = rankList.filter(r => r && userRankIds.includes(r.id));

  if (myRanks.length === 0) {
      return rankList.find(r => r && r.id === 'visitor') || rankList[rankList.length - 1];
  }

  const getRankScore = (rank) => {
      let score = 0;
      if (rank.type === 'free') score += 30;
      else if (rank.type === 'half') score += 20;
      else score += 10;
      if (rank.priority) score += 5;
      return score;
  };

  // myRanks は filter で作った新しい配列なので sort による破壊的変更は安全
  myRanks.sort((a, b) => {
      const scoreA = getRankScore(a);
      const scoreB = getRankScore(b);
      if (scoreA !== scoreB) return scoreB - scoreA;
      const indexA = rankList.indexOf(a);
      const indexB = rankList.indexOf(b);
      return indexA - indexB;
  });

  return myRanks[0];
};

// ==========================================
// ★追加: ランクID配列への正規化（イベントの allowedRanks 等に使う）
//   ・配列（文字列 / {id} / {value} / 入れ子）
//   ・カンマ区切りの文字列
//   のいずれでも「文字列IDの配列」を返す。
//   EventCard.js にあった extractIds はここへ集約した。
// ==========================================
export const toIdArray = (data) => {
    if (data === null || data === undefined || data === '') return [];
    if (Array.isArray(data)) {
        return data
            .flat(Infinity)
            .map(item => {
                if (item && typeof item === 'object') return item.id || item.value || '';
                return typeof item === 'string' ? item : '';
            })
            .map(s => String(s).trim())
            .filter(Boolean);
    }
    if (typeof data === 'string') {
        return data.split(',').map(s => s.trim()).filter(Boolean);
    }
    return [];
};

// ==========================================
// ★追加: 優先予約（先行受付）の判定
//   これまで EventCard.js / App.js がそれぞれ myRankIds を組み立てて
//   priority を見ていたものを1本化する。
//   保有ランクの判定は getUserRankIds() に一任するため、
//   rankIds / rankId / membership / 旧フラグ / rankExpiries の
//   すべてが自動的に反映される。
// ==========================================
export const hasPriorityRank = (user, ranks = []) => {
    const rankList = (Array.isArray(ranks) && ranks.length > 0) ? ranks : DEFAULT_RANKS;
    const myIds = getUserRankIds(user);
    return rankList.some(r => r && r.priority && myIds.includes(r.id));
};

// ==========================================
// ★追加: イベントの参加対象ランク（allowedRanks）の判定
//   allowedRanks が空 / 未設定なら「全員参加可」とみなす。
// ==========================================
export const isRankAllowed = (user, allowedRanks) => {
    const allowed = toIdArray(allowedRanks);
    if (allowed.length === 0) return true;
    const myIds = getUserRankIds(user);
    return myIds.some(id => allowed.includes(id));
};

// ==========================================
// ランク情報の型を正規化するヘルパー
// 一括移行スクリプト（Admin）や保存処理から呼び出して、
// rankId / rankIds をともに「文字列の配列」に揃えるために使う。
// ==========================================
export const normalizeRanks = (u) => {
    if (!u) return { rankIds: ['visitor'], rankId: 'visitor', membership: 'visitor' };

    // ★ 判定ロジックをそのまま再利用する。
    //   getUserRankIds 側で
    //     ・rankIds / rankId の平坦化と型の統一
    //     ・isMonthly / isCollaborator / membership の旧フラグ回収
    //     ・rankExpiries による期限切れの除外
    //   をすべて行っているため、ここで同じ処理を書かない。
    //   （判定用と保存用でロジックが食い違うと、保存のたびに権限が削れる事故になる）
    let ids = getUserRankIds(u);

    // visitor 以外を持っていれば visitor は落とす（AdminUsers.js と同じ考え方）
    if (ids.some(id => id !== 'visitor')) ids = ids.filter(id => id !== 'visitor');
    if (ids.length === 0) ids = ['visitor'];

    const rankId = ids.find(id => id !== 'visitor') || 'visitor';

    return { rankIds: ids, rankId, membership: rankId };
};

// ==========================================
// マイカードの成長
//
// 特訓で振り分けた能力の合計に応じて、自分自身のカード（MYカード）の
// ランクが上がる仕組み。
//
// 【なぜ必要か】
//   ガチャは自分のカードを排出しないため、特訓しても得をするのは
//   「自分のカードを引いた他人」だけ、という状態になっていた。
//   MYカードが成長すれば、鍛えた本人が最も強くなる。
//
//   他人のカードの最高倍率は SSR の ×2.0。
//   MYカードは鍛え続ければ ×2.8 まで伸びるので、
//   「最強のカードは自分を育てること」という関係になる。
// ==========================================
export const MY_CARD_TIERS = [
    { min: 0,    id: 'MY',        name: 'マイカード',        icon: '⭐',   multiplier: 1.0, captainBonus: 0,    color: 'linear-gradient(135deg, #11998e, #38ef7d)' },
    { min: 200,  id: 'MY_PLUS',   name: 'マイカード+',       icon: '⭐⭐',  multiplier: 1.3, captainBonus: 0.02, color: 'linear-gradient(135deg, #0f9b8e, #43e97b, #38f9d7)' },
    { min: 400,  id: 'MY_PP',     name: 'マイカード++',      icon: '⭐⭐⭐', multiplier: 1.7, captainBonus: 0.04, color: 'linear-gradient(135deg, #0575e6, #00f260)' },
    { min: 700,  id: 'MY_ACE',    name: 'マイカード ACE',    icon: '👑',   multiplier: 2.2, captainBonus: 0.07, color: 'linear-gradient(135deg, #f7971e, #ffd200, #00f260)' },
    { min: 1200, id: 'MY_LEGEND', name: 'マイカード LEGEND', icon: '🔥',   multiplier: 2.8, captainBonus: 0.10, color: 'linear-gradient(135deg, #ff0844, #ffb199, #ffd200, #00f260, #0575e6)' }
];

export const sumBaseStats = (baseStats) => {
    const b = baseStats || {};
    return ['offense', 'defense', 'stamina', 'speed', 'technique']
        .reduce((a, k) => a + (Number(b[k]) || 0), 0);
};

// 振り分け合計から MY カードのランクを返す
export const getMyCardTier = (baseStats) => {
    const total = sumBaseStats(baseStats);
    let tier = MY_CARD_TIERS[0];
    MY_CARD_TIERS.forEach(t => { if (total >= t.min) tier = t; });
    return tier;
};

// 次のランクまでの残りポイント（UIで進捗を出すため）
export const getNextMyCardTier = (baseStats) => {
    const total = sumBaseStats(baseStats);
    const next = MY_CARD_TIERS.find(t => t.min > total);
    return next ? { ...next, remain: next.min - total } : null;
};

// ==========================================
// Game & Shop Logic
// ==========================================

// 神社ゲーム：おみくじ設定
export const OMIKUJI_CONFIG = [
  { id: 'daikichi', label: '大吉', icon: '🌸', weight: 5, reward: 500, text: '素晴らしい一日になりそうです！' },
  { id: 'chukichi', label: '中吉', icon: '✨', weight: 20, reward: 200, text: '良いことがありそう。' },
  { id: 'shokichi', label: '小吉', icon: '🍀', weight: 40, reward: 50, text: 'ささやかな幸せが訪れます。' },
  { id: 'kichi', label: '吉', icon: '🍵', weight: 20, reward: 10, text: 'いつも通りが一番です。' },
  { id: 'kyo', label: '凶', icon: '☂️', weight: 10, reward: -50, text: '足元に気をつけて...' },
  { id: 'daikyo', label: '大凶', icon: '⚡', weight: 5, reward: -200, text: '今日はおとなしくしていましょう。' },
];

export const drawOmikuji = () => {
  const totalWeight = OMIKUJI_CONFIG.reduce((acc, item) => acc + item.weight, 0);
  let random = Math.random() * totalWeight;
  for (const item of OMIKUJI_CONFIG) {
    if (random < item.weight) {
      return item;
    }
    random -= item.weight;
  }
  return OMIKUJI_CONFIG[OMIKUJI_CONFIG.length - 1];
};

// ショップ：商品初期設定 (デフォルト)
// 管理画面で設定すればこれらは無視されます
export const SHOP_ITEMS = [
  { 
    id: 'monthly_subscription', 
    name: '正規メンバー権 (1ヶ月)', 
    description: '参加費無料、優先予約権が付与されます。', 
    price: 10000, 
    currency: 'JPY', 
    image: '👑',
    type: 'rank',
    targetId: 'monthly',
    periodDays: 30,
    validityLabel: '有効: 30日間'
  }
];